// Esquema de Vacunación del paciente (ficha 360°): tabla ordenable con edad al
// vacunar, alta desde el catálogo de vacunas (modal con búsqueda y edad
// recomendada), edición/eliminación e impresión.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2, Pencil, Plus, Printer, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { AutoTextarea, Campo, Modal, fieldCls, med, medRpc } from './shared';
import type { Vacuna } from './shared';
import { SortTh, useSort } from '@/features/control-bancario/shared';
import { fechaLocal } from '@/lib/utils/fecha';
import { LOCALE } from '@/lib/utils/regional';

interface VacRow extends Vacuna { comercial: string | null; catalogo_id: string | null }
interface CatVacuna { id: string; nombre: string; edad_recomendada: string | null; orden: number }

interface Form {
  id: string | null; catalogo_id: string | null; vacuna: string; dosis: string; fecha: string;
  comercial: string; lote: string; responsable: string; proxima_fecha: string; observaciones: string;
}

// Edad del paciente en una fecha dada ("X años Y meses Z días")
export const edadAl = (fnac: string | null, fecha: string | null) => {
  if (!fnac || !fecha) return '—';
  const n = new Date(`${fnac.slice(0, 10)}T00:00:00`);
  const f = new Date(`${fecha.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(n.getTime()) || Number.isNaN(f.getTime()) || f < n) return '—';
  let anios = f.getFullYear() - n.getFullYear();
  let meses = f.getMonth() - n.getMonth();
  let dias = f.getDate() - n.getDate();
  if (dias < 0) { dias += new Date(f.getFullYear(), f.getMonth(), 0).getDate(); meses--; }
  if (meses < 0) { anios--; meses += 12; }
  return anios > 0 ? `${anios} años ${meses} meses ${dias} días`
    : meses > 0 ? `${meses} meses ${dias} días` : `${dias} días`;
};

const fmtDDMMYYYY = (v: string | null) => {
  if (!v) return '—';
  const [y, m, d] = v.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};

export function EsquemaVacunacion({ pacienteId, nombre, fechaNacimiento, onVolver }: {
  pacienteId: string; nombre: string; fechaNacimiento: string | null; onVolver: () => void;
}) {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isLoading } = useQuery<VacRow[]>({
    queryKey: ['med-ficha', pacienteId, 'vacunas'],
    queryFn: () => medRpc<VacRow[]>('med_vacunas', { _paciente_id: pacienteId }),
  });
  const { data: catalogo = [] } = useQuery<CatVacuna[]>({
    queryKey: ['med-vacuna-catalogo'],
    queryFn: () => med.list<CatVacuna>('vacuna_catalogo', '?es_activo=is.true&order=orden,nombre'),
    staleTime: Infinity,
  });

  const sort = useSort<VacRow>(rows, (r, k) => {
    if (k === 'edad') return r.fecha; // la edad al vacunar ordena por fecha
    return (r as unknown as Record<string, string | null>)[k] ?? null;
  }, 'fecha', 'desc');

  const [pick, setPick] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [form, setForm] = useState<Form | null>(null);
  const set = (k: keyof Form, v: string) => setForm((f) => (f ? { ...f, [k]: v } : f));

  const catFiltrado = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return q ? catalogo.filter((c) => c.nombre.toLowerCase().includes(q)) : catalogo;
  }, [catalogo, busqueda]);

  const elegir = (c: CatVacuna) => {
    setPick(false);
    setForm({
      id: null, catalogo_id: c.id, vacuna: c.nombre === 'Otros' ? '' : c.nombre,
      dosis: '', fecha: fechaLocal(), comercial: '', lote: '', responsable: '', proxima_fecha: '', observaciones: '',
    });
  };
  const editar = (r: VacRow) => setForm({
    id: r.id, catalogo_id: r.catalogo_id, vacuna: r.vacuna, dosis: r.dosis ?? '', fecha: r.fecha?.slice(0, 10) ?? fechaLocal(),
    comercial: r.comercial ?? '', lote: r.lote ?? '', responsable: r.responsable ?? '',
    proxima_fecha: r.proxima_fecha?.slice(0, 10) ?? '', observaciones: r.observaciones ?? '',
  });

  const guardar = useMutation({
    mutationFn: () => {
      if (!form) return Promise.reject(new Error('Sin datos'));
      const payload = {
        vacuna: form.vacuna.trim(), catalogo_id: form.catalogo_id, dosis: form.dosis.trim() || null,
        fecha: form.fecha, comercial: form.comercial.trim() || null, lote: form.lote.trim() || null,
        responsable: form.responsable.trim() || null, proxima_fecha: form.proxima_fecha || null,
        observaciones: form.observaciones.trim() || null,
      };
      return form.id ? med.update('vacuna', form.id, payload) : med.create('vacuna', { ...payload, paciente_id: pacienteId });
    },
    onSuccess: () => { toast.success('Vacuna guardada'); setForm(null); void qc.invalidateQueries({ queryKey: ['med-ficha', pacienteId] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const eliminar = useMutation({
    mutationFn: (id: string) => med.update('vacuna', id, { deleted_at: new Date().toISOString() }),
    onSuccess: () => { toast.success('Vacuna eliminada'); void qc.invalidateQueries({ queryKey: ['med-ficha', pacienteId] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const imprimir = () => {
    const esc = (v: unknown) => String(v ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));
    const filas = sort.sorted.map((r, i) => `<tr>
      <td class="c">${i + 1}</td><td>${esc(r.vacuna)}</td><td>${esc(r.dosis || '—')}</td>
      <td class="c">${esc(fmtDDMMYYYY(r.fecha))}</td><td class="c">${esc(edadAl(fechaNacimiento, r.fecha))}</td>
      <td>${esc(r.comercial || '—')}</td><td>${esc(r.lote || '—')}</td><td>${esc(r.responsable || '—')}</td>
    </tr>`).join('');
    const html = `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>Esquema de vacunación</title>
      <style>
        *{font-family:Arial,Helvetica,sans-serif}
        h1{font-size:16px;margin:0 0 2px} .sub{font-size:12px;color:#555;margin:0 0 12px}
        table{border-collapse:collapse;width:100%;font-size:11px}
        th,td{border:1px solid #ccc;padding:4px 6px;text-align:left}
        th{background:#f0f0f0} .c{text-align:center}
        @media print{@page{margin:12mm}}
      </style></head><body>
      <h1>Esquema de Vacunación</h1>
      <p class="sub">Paciente: <b>${esc(nombre)}</b> · Total: <b>${sort.sorted.length}</b> · Emitido: ${esc(new Date().toLocaleString(LOCALE))}</p>
      <table><thead><tr>
        <th class="c">#</th><th>Vacuna</th><th>Dosis</th><th class="c">Fecha Vacunación</th>
        <th class="c">Edad al vacunar</th><th>Comercial</th><th>Lote</th><th>Quién Aplica</th>
      </tr></thead><tbody>${filas || '<tr><td colspan="8" class="c">Sin vacunas registradas</td></tr>'}</tbody></table>
      <script>window.onload=function(){window.print();}</script>
      </body></html>`;
    const win = window.open('', '_blank');
    if (!win) { toast.error('Permite las ventanas emergentes para imprimir'); return; }
    win.document.write(html); win.document.close();
  };

  return (
    <div className="space-y-4">
      {dlg.ui}
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onVolver}><ArrowLeft className="w-4 h-4 mr-1" /> Volver a la ficha</Button>
        <h2 className="text-lg font-semibold">Vacunas</h2>
      </div>

      <section>
        <div className="flex items-center justify-between rounded-t-md bg-primary/10 px-4 py-2">
          <span className="text-sm font-semibold">Esquema de Vacunación</span>
          <Button size="sm" onClick={() => { setBusqueda(''); setPick(true); }}><Plus className="w-4 h-4 mr-1" /> Agregar</Button>
        </div>
        <div className="rounded-b-md border border-t-0 bg-card px-4 py-3 space-y-3 overflow-x-auto">
          {isLoading && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-xs text-muted-foreground">
                <th className="py-2 pr-3 text-left">N°</th>
                <SortTh col="vacuna" sort={sort}>Vacuna</SortTh>
                <SortTh col="dosis" sort={sort}>Dosis</SortTh>
                <SortTh col="fecha" sort={sort}>Fecha Vacunación (DD/MM/YYYY)</SortTh>
                <SortTh col="edad" sort={sort}>Edad al vacunar</SortTh>
                <SortTh col="comercial" sort={sort}>Comercial</SortTh>
                <SortTh col="lote" sort={sort}>Lote</SortTh>
                <SortTh col="responsable" sort={sort}>Quién Aplica</SortTh>
                <th className="py-2 text-right text-xs">Acción</th>
              </tr>
            </thead>
            <tbody>
              {sort.sorted.length === 0 && !isLoading && (
                <tr><td colSpan={9} className="py-6 text-center text-primary/80">No existen vacunas registradas</td></tr>
              )}
              {sort.sorted.map((r, i) => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3">{i + 1}</td>
                  <td className="py-2 pr-3 font-medium">{r.vacuna}</td>
                  <td className="py-2 pr-3">{r.dosis || '—'}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{fmtDDMMYYYY(r.fecha)}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{edadAl(fechaNacimiento, r.fecha)}</td>
                  <td className="py-2 pr-3">{r.comercial || '—'}</td>
                  <td className="py-2 pr-3">{r.lote || '—'}</td>
                  <td className="py-2 pr-3">{r.responsable || '—'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" aria-label="Editar" onClick={() => editar(r)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar" aria-label="Eliminar"
                      onClick={async () => { if (!(await dlg.confirmar(`¿Eliminar la vacuna "${r.vacuna}"?`, { peligro: true }))) return; eliminar.mutate(r.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Button variant="outline" size="sm" onClick={imprimir}><Printer className="w-4 h-4 mr-1" /> Imprimir</Button>
        </div>
      </section>

      {/* Modal 1: catálogo de vacunas (búsqueda + edad recomendada) */}
      {pick && (
        <Modal title="Vacunas" onClose={() => setPick(false)} wide>
          <div className="space-y-3">
            <Campo label="Buscar">
              <div className="relative">
                <input className={`${fieldCls} pr-8`} placeholder="Escribe para filtrar…" autoFocus
                  value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
                <Search className="w-4 h-4 absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              </div>
            </Campo>
            <div className="max-h-[50vh] overflow-y-auto border rounded-md">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted">
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className="py-2 px-3">Descripción</th>
                    <th className="py-2 px-3">Edad Recomendada</th>
                  </tr>
                </thead>
                <tbody>
                  {catFiltrado.length === 0 && <tr><td colSpan={2} className="py-6 text-center text-muted-foreground">Sin resultados</td></tr>}
                  {catFiltrado.map((c) => (
                    <tr key={c.id} className="border-t cursor-pointer hover:bg-primary/5" onClick={() => elegir(c)} title="Seleccionar">
                      <td className="py-2 px-3 font-medium">{c.nombre}</td>
                      <td className="py-2 px-3 text-muted-foreground">{c.edad_recomendada ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={() => setPick(false)}>Cerrar</Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal 2: registro / edición de la aplicación */}
      {form && (
        <Modal title={form.id ? 'Editar vacuna' : 'Registrar vacuna'} onClose={() => setForm(null)} wide>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Vacuna *" className="col-span-2"><input className={fieldCls} value={form.vacuna} onChange={(e) => set('vacuna', e.target.value)} /></Campo>
              <Campo label="Dosis"><input className={fieldCls} placeholder="1ra, 2da, refuerzo…" value={form.dosis} onChange={(e) => set('dosis', e.target.value)} /></Campo>
              <Campo label="Fecha de vacunación *"><input type="date" className={fieldCls} value={form.fecha} onChange={(e) => set('fecha', e.target.value)} /></Campo>
              <Campo label="Comercial"><input className={fieldCls} placeholder="Nombre comercial" value={form.comercial} onChange={(e) => set('comercial', e.target.value)} /></Campo>
              <Campo label="Lote"><input className={fieldCls} value={form.lote} onChange={(e) => set('lote', e.target.value)} /></Campo>
              <Campo label="Quién aplica"><input className={fieldCls} value={form.responsable} onChange={(e) => set('responsable', e.target.value)} /></Campo>
              <Campo label="Próxima dosis"><input type="date" className={fieldCls} value={form.proxima_fecha} onChange={(e) => set('proxima_fecha', e.target.value)} /></Campo>
              <Campo label="Observaciones" className="col-span-2"><AutoTextarea value={form.observaciones} onChange={(e) => set('observaciones', e.target.value)} /></Campo>
            </div>
            {fechaNacimiento && form.fecha && (
              <p className="text-xs text-muted-foreground">Edad al vacunar: <b>{edadAl(fechaNacimiento, form.fecha)}</b></p>
            )}
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" size="sm" onClick={() => setForm(null)}>Cancelar</Button>
              <Button size="sm" isLoading={guardar.isPending} disabled={!form.vacuna.trim() || !form.fecha} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
